/* global fetch */

/**
 * The terms-of-use gate, shared by the three places a person signs in: the
 * browser portal (signup.html), the downloads page, and the Office taskpane.
 * The decision and the request live here so the pages only do layout.
 *
 * The backend decides. `session.terms.acceptance_required` is computed
 * server-side from the configured version and the user's recorded acceptance;
 * nothing here compares version strings, so no client can be more lenient
 * than the server about who has agreed to what.
 */

import { API_CONFIG } from "../config/api.js";
import { loadAuthConfig } from "./entraClient.js";
import { getRequestAuthHeaders } from "./session.js";
import { parseAuthResponse } from "./webAttempt.js";

/** The server refused an acceptance because the version on screen is old. */
export const TERMS_VERSION_STALE = "TERMS_VERSION_STALE";

/** The server refused an app call because this session has not accepted. */
export const TERMS_ACCEPTANCE_REQUIRED = "TERMS_ACCEPTANCE_REQUIRED";

/**
 * Whether this session must see the acceptance screen before going further.
 *
 * `runtime.terms.version` is the "gate is on" signal. A backend that does not
 * publish one -- the gate switched off, or an older backend that predates it
 * -- never blocks anyone. When it IS on, a session missing its `terms` block
 * fails closed: an unknown acceptance state is treated as unaccepted, for the
 * same reason the connector gate treats an unknown pairing state as unpaired.
 *
 * @param {object|null} session   a /session or /complete response
 * @param {object|null} runtime   the /auth/config response
 * @returns {boolean}
 */
export function termsAcceptanceRequired(session, runtime) {
  if (!runtime?.terms?.version) return false;
  if (!session?.user) return false;
  const terms = session.terms;
  if (!terms || typeof terms.acceptance_required !== "boolean") return true;
  return terms.acceptance_required;
}

/**
 * Whether this person accepted an EARLIER version, so the screen should say
 * the terms changed rather than greet them as if for the first time. The
 * backend reports `accepted_version` on every session view.
 */
export function termsWereUpdated(session) {
  return Boolean(session?.terms?.accepted_version);
}

/**
 * Points every `a[data-terms-document="<id>"]` inside `root` at the configured
 * document of that id. The links are authored in the HTML so the copy stays
 * readable in the template; only the URLs are runtime data. An id the config
 * does not know keeps its text but loses its href, rather than pointing at
 * something stale.
 *
 * @returns {number} how many configured documents were bound
 */
export function bindTermsDocumentLinks(root, runtime) {
  const documents = Array.isArray(runtime?.terms?.documents) ? runtime.terms.documents : [];
  const byId = new Map(documents.map((doc) => [doc.id, doc]));
  let bound = 0;
  root.querySelectorAll("a[data-terms-document]").forEach((link) => {
    const doc = byId.get(link.dataset.termsDocument);
    if (doc?.url) {
      link.href = doc.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      bound += 1;
    } else {
      link.removeAttribute("href");
    }
  });
  return bound;
}

/**
 * Records acceptance for the signed-in session. Same credential shape as
 * sign-out: the HttpOnly cookie plus the CSRF header. `version` is the one
 * that was on screen; the server refuses a stale one rather than upgrading it.
 *
 * Resolves to the backend's `{ user, terms }` view.
 */
export async function acceptTerms({ version }) {
  if (!version) throw new Error("There is no terms version to accept");
  const response = await fetch(`${API_CONFIG.baseURL}/auth/terms/accept`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getRequestAuthHeaders("POST") },
    body: JSON.stringify({ version }),
  });
  return parseAuthResponse(response);
}

/**
 * Wires one acceptance panel and returns show/hide for it. Wire once, show as
 * often as needed: showing re-reads the runtime (version and links) and resets
 * the controls, so a second sign-in in the same page never sees a pre-ticked
 * box or a stale version.
 *
 * Markup contract, looked up inside `panel` so two panels can coexist:
 *   [data-terms-checkbox]    the single "I agree" box
 *   [data-terms-accept]      continue; disabled until the box is ticked
 *   [data-terms-decline]     optional: sign out / not now
 *   [data-terms-status]      optional: one line for progress and errors
 *   a[data-terms-document]   one per document, see bindTermsDocumentLinks
 *   [data-terms-updated-text]  optional: the element's text when the terms
 *                              CHANGED since the person last accepted; its
 *                              own text is the first-time wording
 *
 * The links deliberately live OUTSIDE the checkbox's <label>: a link inside a
 * label also toggles the box, so reading the terms would count as agreeing.
 *
 * `show(runtime, { updated })`: pass `updated: true` for someone who accepted
 * an earlier version (see termsWereUpdated), so the screen says the terms
 * changed instead of "one more step".
 *
 * `reloadRuntime` fetches /auth/config again. It is used when the server
 * refuses the acceptance as stale: the documents changed while this screen was
 * open, so the panel re-reads them, clears the box and asks again -- instead of
 * resending the old version on every click until the page is reloaded.
 */
export function createTermsPanel(
  panel,
  { onAccepted, onDeclined, reloadRuntime = loadAuthConfig } = {}
) {
  const checkbox = panel.querySelector("[data-terms-checkbox]");
  const accept = panel.querySelector("[data-terms-accept]");
  const decline = panel.querySelector("[data-terms-decline]");
  const status = panel.querySelector("[data-terms-status]");
  // Each wording-aware element with its first-time text, read once from the
  // markup, so a later first-time show can put it back.
  const wording = Array.from(panel.querySelectorAll("[data-terms-updated-text]")).map(
    (element) => ({ element, firstTime: element.textContent })
  );
  let version = "";
  let updated = false;

  function show(runtime, options = {}) {
    version = runtime?.terms?.version || "";
    updated = Boolean(options.updated);
    for (const { element, firstTime } of wording) {
      element.textContent = updated ? element.dataset.termsUpdatedText : firstTime;
    }
    bindTermsDocumentLinks(panel, runtime);
    reset();
    panel.hidden = false;
  }

  /**
   * Re-reads the documents after a stale refusal. Resolves true when the panel
   * is showing the new version and waiting for a fresh tick, false when the
   * gate was switched off meanwhile and the caller may simply continue.
   */
  async function refreshAfterStale() {
    const runtime = await reloadRuntime();
    if (!runtime?.terms?.version) return false;
    show(runtime, { updated });
    setStatus(
      "The terms were updated while this screen was open. Review them and tick the box again.",
      true
    );
    return true;
  }

  function setStatus(message, error = false) {
    if (!status) return;
    status.textContent = message || "";
    status.classList.toggle("error", error);
  }

  function reset() {
    checkbox.checked = false;
    checkbox.disabled = false;
    accept.disabled = true;
    setStatus("");
  }

  checkbox.addEventListener("change", () => {
    accept.disabled = !checkbox.checked;
  });

  accept.addEventListener("click", async () => {
    if (!checkbox.checked) return;
    accept.disabled = true;
    checkbox.disabled = true;
    setStatus("Saving...");
    try {
      const result = await acceptTerms({ version });
      // The server is the source of truth; a 200 that still says "required"
      // means nothing was recorded, and continuing would skip the gate.
      if (result?.terms?.acceptance_required !== false) {
        throw new Error("Your acceptance could not be recorded. Try again.");
      }
      setStatus("");
      await onAccepted?.(result);
    } catch (error) {
      if (error?.code === TERMS_VERSION_STALE) {
        try {
          if (await refreshAfterStale()) return;
          panel.hidden = true;
          await onAccepted?.(null);
          return;
        } catch (reloadError) {
          error = reloadError;
        }
      }
      setStatus(error.message || "Your acceptance could not be recorded", true);
      checkbox.disabled = false;
      accept.disabled = !checkbox.checked;
    }
  });

  if (decline) {
    decline.addEventListener("click", () => {
      onDeclined?.();
    });
  }

  return {
    show,
    hide() {
      panel.hidden = true;
    },
    isShowing() {
      return !panel.hidden;
    },
  };
}
